function requireUniqueIds(values) {
  const byId = new Map();
  (values || []).forEach((value) => {
    if (!value?.id) throw new TypeError("Every Classification Value requires an id.");
    if (byId.has(value.id)) throw new Error(`Duplicate Classification Value id: ${value.id}`);
    byId.set(value.id, value);
  });
  return byId;
}

export function getClassificationAncestors(values, valueId) {
  const byId = requireUniqueIds(values);
  const ancestors = [];
  const visited = new Set([valueId]);
  let current = byId.get(valueId);

  while (current?.parentValueId ?? current?.parent_value_id) {
    const parentId = current.parentValueId ?? current.parent_value_id;
    if (visited.has(parentId)) throw new Error("Classification hierarchy contains a cycle.");
    const parent = byId.get(parentId);
    if (!parent) throw new Error(`Missing parent Classification Value: ${parentId}`);
    ancestors.unshift(parent);
    visited.add(parentId);
    current = parent;
  }

  return ancestors;
}

export function getClassificationDescendants(values, valueId) {
  const byId = requireUniqueIds(values);
  if (!byId.has(valueId)) return [];

  const childrenByParent = new Map();
  byId.forEach((value) => {
    const parentId = value.parentValueId ?? value.parent_value_id ?? null;
    if (!parentId) return;
    const children = childrenByParent.get(parentId) ?? [];
    children.push(value);
    childrenByParent.set(parentId, children);
  });

  const descendants = [];
  const visited = new Set([valueId]);
  const visit = (parentId) => {
    (childrenByParent.get(parentId) ?? []).forEach((child) => {
      if (visited.has(child.id)) throw new Error("Classification hierarchy contains a cycle.");
      visited.add(child.id);
      descendants.push(child);
      visit(child.id);
    });
  };
  visit(valueId);
  return descendants;
}

export function wouldCreateClassificationCycle(values, valueId, parentValueId) {
  if (!parentValueId) return false;
  if (valueId === parentValueId) return true;
  return getClassificationDescendants(values, valueId).some(
    (value) => value.id === parentValueId
  );
}

export function getClassificationPath(values, valueId, separator = " > ") {
  const byId = requireUniqueIds(values);
  const value = byId.get(valueId);
  if (!value) return "";
  return [...getClassificationAncestors(values, valueId), value]
    .map((item) => item.name)
    .join(separator);
}

export function calculateClassificationRollups(values, directAmountsByValueId) {
  const byId = requireUniqueIds(values);
  const totals = new Map();

  byId.forEach((_, id) => totals.set(id, Number(directAmountsByValueId?.[id] ?? 0)));

  byId.forEach((_, id) => {
    const directAmount = Number(directAmountsByValueId?.[id] ?? 0);
    if (!directAmount) return;
    getClassificationAncestors(values, id).forEach((ancestor) => {
      totals.set(ancestor.id, (totals.get(ancestor.id) ?? 0) + directAmount);
    });
  });

  return Object.fromEntries(totals);
}

export function flattenClassificationHierarchy(values) {
  const byId = requireUniqueIds(values);
  const childrenByParent = new Map();
  byId.forEach((value) => {
    const parentId = value.parentValueId ?? value.parent_value_id ?? null;
    const children = childrenByParent.get(parentId) ?? [];
    children.push(value);
    childrenByParent.set(parentId, children);
  });
  childrenByParent.forEach((children) =>
    children.sort(
      (left, right) =>
        Number(left.sort_order ?? left.sortOrder ?? 0) -
          Number(right.sort_order ?? right.sortOrder ?? 0) ||
        String(left.name).localeCompare(String(right.name))
    )
  );

  const rows = [];
  const visited = new Set();
  const visit = (parentId, depth) => {
    (childrenByParent.get(parentId) ?? []).forEach((value) => {
      if (visited.has(value.id)) throw new Error("Classification hierarchy contains a cycle.");
      visited.add(value.id);
      rows.push({ ...value, depth });
      visit(value.id, depth + 1);
    });
  };
  visit(null, 0);

  if (visited.size !== byId.size) {
    throw new Error("Classification hierarchy contains an orphan or cycle.");
  }
  return rows;
}

export function getClassificationCodePath(values, valueId, separator = " > ") {
  const byId = requireUniqueIds(values);
  const value = byId.get(valueId);
  if (!value) return "";
  return [...getClassificationAncestors(values, valueId), value]
    .map((item) => String(item.code || "").trim() || item.name)
    .join(separator);
}

export function getClassificationLeafValues(values) {
  const byId = requireUniqueIds(values);
  const parentIds = new Set();
  byId.forEach((value) => {
    const parentId = value.parentValueId ?? value.parent_value_id ?? null;
    if (parentId) parentIds.add(parentId);
  });
  return flattenClassificationHierarchy(values).filter(
    (value) => !parentIds.has(value.id)
  );
}
